import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { useAuth } from '@/app/providers';
import { Avatar, Spinner } from '@/components/ui';
import { timeAgo } from '@/lib/utils';
import { stripHandle } from '@/features/profile/helpers';
import { useMessageSearch } from '../hooks/useDirectMessages';

/** Discord-style inbox search — finds messages across the user's chat history. */
export function InboxSearch() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState('');
  const [focused, setFocused] = useState(false);
  const { data: hits = [], isFetching } = useMessageSearch(user?.id, q);
  const open = focused && q.trim().length >= 2;

  function go(conversationId: string) {
    setQ('');
    setFocused(false);
    navigate(`/dm/${conversationId}`);
  }

  return (
    <div className="relative mt-4 max-w-[460px]">
      <div className="flex h-10 items-center gap-2.5 rounded-pill border border-white/10 bg-white/[0.04] px-4 transition-colors focus-within:border-white/25">
        <Search size={16} className="flex-none text-white/45" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => window.setTimeout(() => setFocused(false), 150)}
          placeholder="Search messages"
          autoComplete="off"
          className="min-w-0 flex-1 bg-transparent text-[13px] font-semibold text-white outline-none placeholder:text-white/40"
        />
        {q && (
          <button
            type="button"
            onClick={() => setQ('')}
            aria-label="Clear search"
            className="flex h-6 w-6 flex-none items-center justify-center text-white/40 transition-colors hover:text-white"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && (
        <div className="chat-scroll absolute left-0 right-0 top-[calc(100%+8px)] z-30 max-h-[360px] overflow-y-auto rounded-xl border border-white/10 bg-[rgba(16,16,22,0.98)] p-1.5 shadow-[0_24px_60px_rgba(0,0,0,0.6)] backdrop-blur-2xl">
          {isFetching && !hits.length ? (
            <div className="grid place-items-center py-6">
              <Spinner className="h-4 w-4" />
            </div>
          ) : hits.length ? (
            hits.map((hit) => {
              const peerName =
                hit.peer?.display_name || stripHandle(hit.peer?.handle ?? null) || 'WINR member';
              const authorName =
                hit.author?.display_name || stripHandle(hit.author?.handle ?? null) || 'Someone';
              return (
                <button
                  key={hit.messageId}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => go(hit.conversationId)}
                  className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-white/[0.06]"
                >
                  <Avatar src={hit.peer?.avatar_url ?? null} name={peerName} size={34} className="border border-white/10" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-[13px] font-bold text-white">{peerName}</span>
                      <span className="flex-none text-[11px] font-semibold text-white/40">{timeAgo(hit.createdAt)}</span>
                    </div>
                    <div className="truncate text-[12px] font-medium text-white/55">
                      <span className="font-semibold text-white/70">{authorName}:</span> {hit.body}
                    </div>
                  </div>
                </button>
              );
            })
          ) : (
            <div className="px-3 py-6 text-center text-[12px] font-semibold text-white/45">
              No messages match “{q.trim()}”.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
